import { formatUpperCase } from '../../../../utils/formatTitleCase';
// pages/master-data/driver/form/DriverForm.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Typography, Switch, Button, Grid, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { fleetApiGo } from '../../../../api/axiosClient';
import PickerField from '../../../../components/PickerField';
import { useLanguage } from '../../../../utils/i18n';
import blankPhoto from 'assets/images/icons/blank-photo.png';
import { useCompanyList } from '../../../../stores/companyStore';
import MasterCompanyAddEdit from '../../company/MasterCompanyAddEdit';

export default function DriverForm({ formik }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);

  const { companyOptions: companyOptionsFromApi = [] } = useCompanyList();

  const companyOptions = companyOptionsFromApi;

  const getOptionId = useCallback((o) => (o == null ? o : (o.value ?? o.id ?? o)), []);
  const getOptionLabel = useCallback((o) => (o == null ? '' : (o.label ?? o.name ?? String(o.value ?? o.id ?? o))), []);

  const findOptionByValue = useCallback(
    (options = [], value) => {
      if (value == null || value === '') return null;
      return (
        (options || []).find((o) => {
          try {
            return String(getOptionId(o)) === String(value);
          } catch {
            return false;
          }
        }) ?? null
      );
    },
    [getOptionId]
  );

  const handleGetImage = async (imageURL) => {
    try {
      await fleetApiGo.head(imageURL);
      return imageURL;
    } catch (error) {
      console.log(error);
      return blankPhoto.src || blankPhoto;
    }
  };

  useEffect(() => {
    const base64Val = formik.values._image_base64;
    if (base64Val && base64Val.startsWith('data:')) {
      setPhotoPreview(base64Val);
      return;
    }

    const validateExistingImage = async () => {
      const driverId = formik.values.id;
      const companyId = localStorage.getItem('companyId');
      const token = localStorage.getItem('apiKey');

      if (!driverId) {
        setPhotoPreview(null);
        return;
      }

      setImgLoading(true);
      const timeNow = new Date().getTime();
      const photoPath = `/images/driver/${companyId}/${driverId}.png?apikey=${token}&t=${timeNow}`;
      const fullUrl = `${fleetApiGo.defaults.baseURL}${photoPath}`;

      const finalUrl = await handleGetImage(photoPath);

      if (finalUrl === photoPath) {
        setPhotoPreview(fullUrl);
      } else {
        setPhotoPreview(null);
      }
      setImgLoading(false);
    };

    validateExistingImage();
  }, [formik.values._image_base64, formik.values.id]);

  useEffect(() => {
    if (!formik.values._image_file && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [formik.values._image_file]);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    formik.setFieldValue('_image_file', f);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      formik.setFieldValue('_image_base64', result);
      setPhotoPreview(result);
    };
    reader.readAsDataURL(f);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getDayjsValue = (val) => {
    if (!val) return dayjs();
    const d = dayjs(val);
    return d.isValid() ? d : dayjs();
  };

  useEffect(() => {
    if (!formik.values.id) {
      if (!formik.values.birth_date) {
        formik.setFieldValue('birth_date', dayjs().format('YYYY-MM-DD'));
      }
      if (!formik.values.hired_date) {
        formik.setFieldValue('hired_date', dayjs().format('YYYY-MM-DD'));
      }
    }
    if (!formik.values.company) {
      const storedCompanyId = localStorage.getItem('companyId');

      // Pastikan ada companyId di local storage dan bukan "all"
      if (storedCompanyId && storedCompanyId !== 'all') {
        formik.setFieldValue('company', storedCompanyId);

        // Cari nama company agar picker menampilkan label yang benar
        if (companyOptions && companyOptions.length > 0) {
          const match = findOptionByValue(companyOptions, storedCompanyId);
          if (match) {
            formik.setFieldValue('company_name', getOptionLabel(match));
          }
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.id]);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label={t('masterDriver.driverName', 'Driver Name')}
            name="name"
            value={formik.values.name ?? ''}
            onChange={(e) => {
              formik.setFieldValue('name', formatUpperCase(e.target.value));
            }}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.name && formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <PickerField
            label={t('general.company', 'Company') + '*'}
            value={formik.values.company}
            options={companyOptions}
            onChange={(opt) => {
              const selected = findOptionByValue(companyOptions, opt) ?? (typeof opt === 'object' ? opt : null);
              const id = selected ? getOptionId(selected) : (opt ?? null);
              formik.setFieldValue('company', id);
              if (selected) formik.setFieldValue('company_name', getOptionLabel(selected));
              formik.setFieldValue('driver_license', []);
            }}
            allowAdd
            AddComponent={MasterCompanyAddEdit}
            error={Boolean(formik.touched.company && formik.errors.company)}
            helperText={formik.touched.company && formik.errors.company}
            getOptionId={getOptionId}
            getOptionLabel={getOptionLabel}
            dialogTitle={t('general.selectCompany', 'Select Company')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label="NIK"
            name="ktp_no"
            value={formik.values.ktp_no ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.ktp_no && formik.errors.ktp_no)}
            helperText={formik.touched.ktp_no && formik.errors.ktp_no}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label={t('masterDriver.code', 'Driver Code')}
            name="driver_code"
            value={formik.values.driver_code ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.driver_code && formik.errors.driver_code)}
            helperText={formik.touched.driver_code && formik.errors.driver_code}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label={t('masterUser.phone', 'Phone')}
            name="mobile_no"
            value={formik.values.mobile_no ?? ''}
            onChange={formik.handleChange}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label={t('masterDriver.driverType', 'Driver Type')}
            name="driver_type"
            value={formik.values.driver_type ?? ''}
            onChange={formik.handleChange}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formik.values.email ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.email && formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            required
            InputLabelProps={{
              sx: {
                '& .MuiFormLabel-asterisk': {
                  color: 'error.main'
                }
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <DatePicker
            format="DD-MM-YYYY"
            fullWidth
            label={t('masterDriver.hiredDate', 'Hired Date')}
            name="hired_date"
            value={getDayjsValue(formik.values.hired_date)}
            onChange={(newValue) => {
              formik.setFieldValue('hired_date', newValue ? newValue.format('YYYY-MM-DD') : '');
            }}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                InputLabelProps: {
                  sx: {
                    '& .MuiFormLabel-asterisk': {
                      color: 'error.main'
                    }
                  }
                }
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} mt={3}>
        <Grid size={{ xs: 6, md: 6 }}>
          <Grid>
            <TextField
              fullWidth
              label={t('masterDriver.birthPlace', 'Birth Place')}
              name="birth_place"
              value={formik.values.birth_place ?? ''}
              onChange={formik.handleChange}
              required
              InputLabelProps={{
                sx: {
                  '& .MuiFormLabel-asterisk': {
                    color: 'error.main'
                  }
                }
              }}
            />
          </Grid>
          <Grid mt={3}>
            <DatePicker
              format="DD-MM-YYYY"
              fullWidth
              label={t('masterDriver.birthDate', 'Birth Date')}
              name="birth_date"
              value={getDayjsValue(formik.values.birth_date)}
              onChange={(newValue) => {
                formik.setFieldValue('birth_date', newValue ? newValue.format('YYYY-MM-DD') : '');
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  InputLabelProps: {
                    sx: {
                      '& .MuiFormLabel-asterisk': {
                        color: 'error.main'
                      }
                    }
                  }
                }
              }}
            />
          </Grid>
        </Grid>
        <Grid container size={{ xs: 6, md: 6 }} spacing={3}>
          <Grid size={{ xs: 6, md: 6 }}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />

            {(formik.values.id || photoPreview) && (
              <Box>
                <Box
                  sx={{
                    height: 180,
                    width: 240,
                    borderRadius: '12px',
                    border: 2,
                    mt: 1,
                    borderColor: photoPreview ? 'primary.main' : 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    position: 'relative',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {imgLoading ? (
                    <CircularProgress size={24} />
                  ) : photoPreview ? (
                    <Box
                      component="img"
                      src={photoPreview}
                      alt="Driver"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.src = blankPhoto?.src || blankPhoto;
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'grey.400' }}>
                      <AttachFileIcon sx={{ fontSize: 40 }} />
                      <Typography variant="caption" display="block">
                        {t('general.noImage', 'No Image')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Box mt={!formik.values.id && !photoPreview ? 0 : 2} sx={{ display: 'flex', gap: 3 }}>
              <Button startIcon={<AttachFileIcon />} onClick={openFileDialog} variant="outlined">
                {t('buttonTitle.upload', 'Upload Photo')}
              </Button>
              {formik.values._image_file ? (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {formik.values._image_file.name}
                </Typography>
              ) : null}
            </Box>
          </Grid>
          <Grid size={{ xs: 6, md: 6 }}>
            <Grid sx={{ alignItems: 'left' }}>
              <Typography variant="body2">{t('general.status', 'Status')}</Typography>
              <Switch
                checked={Boolean(formik.values.status === undefined ? true : formik.values.status)}
                onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                color="primary"
              />
            </Grid>
            <Grid mt={3}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('masterDriver.activePOD', 'Active POD')}
              </Typography>
              <Switch
                checked={Boolean(formik.values.is_pod_active)}
                onChange={(e) => formik.setFieldValue('is_pod_active', e.target.checked)}
                color="primary"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}

DriverForm.propTypes = {
  formik: PropTypes.object.isRequired
};
